import { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { postLoginPath, readPostLoginFrom, clearPostLoginFrom } from "@/lib/postLogin";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, UserRound, KeyRound } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const routerState = useLocation().state;
  const [from] = useState(() => readPostLoginFrom(routerState, window.sessionStorage));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    clearPostLoginFrom(window.sessionStorage);
    return <Navigate to={postLoginPath(user.role, from)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      clearPostLoginFrom(window.sessionStorage);
      navigate(postLoginPath(res.user.role, from), { replace: true });
    } else {
      // Rate-limit message from backend is already in Dutch-friendly English; translate on the fly.
      const raw = res.error || "";
      const dutch = /too many login attempts/i.test(raw)
        ? raw.replace(/Too many login attempts\. Please try again in (\d+) seconds\./i,
            "Te veel loginpogingen. Probeer het over $1 seconden opnieuw.")
        : /invalid email or password/i.test(raw)
        ? "Onjuist e-mailadres of wachtwoord."
        : /not authenticated|user not found/i.test(raw)
        ? "Geen toegang. Neem contact op met de beheerder."
        : raw;
      setError(dutch);
    }
  };

  return (
    <div className="min-h-screen bg-tw-black flex flex-col" style={{ backgroundImage: "url('/login-bg.webp')", backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="h-1 w-full bg-tw-yellow" />

      <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-10">
        <div className="w-full max-w-md">
          {/* Brand block */}
          <div className="mb-8 sm:mb-10">
            <div className="flex items-baseline gap-3 mb-5">
              <div className="w-3 h-16 bg-tw-yellow shrink-0" aria-hidden />
              <div>
                <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-none tracking-tight">
                  TW1 Brain Intake
                </h1>
                <div className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-tw-yellow mt-2 uppercase">
                  Voice-to-vault
                </div>
              </div>
            </div>
            <p className="text-sm text-tw-muted max-w-sm leading-relaxed">
              Leg managementkennis vast terwijl die ontstaat. Privé, gestructureerd en terug te vinden.
            </p>
          </div>

          {/* Login card */}
          <form
            onSubmit={handleSubmit}
            className="bg-tw-surface border border-tw p-6 sm:p-8 space-y-6"
            data-testid="login-form"
          >
            <div className="flex items-center justify-between border-b border-tw pb-3">
              <div className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight">
                Welkom <span className="text-tw-yellow">terug.</span>
              </div>
              <span
                className="inline-flex items-center gap-1.5 border border-tw-yellow text-tw-yellow px-2 py-1 font-mono text-[9px] tracking-[0.3em] uppercase"
                data-testid="login-secure-badge"
              >
                <ShieldCheck size={11} strokeWidth={2.8} />
                Beveiligd
              </span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-tw-muted">
                <UserRound size={11} strokeWidth={2.5} />
                E-mailadres
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="bg-tw-black border-tw text-white h-12 rounded-none focus:border-tw-yellow focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder="naam@truckwash1group.nl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-tw-muted">
                <KeyRound size={11} strokeWidth={2.5} />
                Wachtwoord
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="bg-tw-black border-tw text-white h-12 rounded-none focus:border-tw-yellow focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="border border-tw-red bg-tw-red/10 text-tw-red text-sm px-3 py-2 font-medium"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="tw-press w-full h-12 rounded-none bg-tw-yellow text-black hover:bg-[#FFDE33] uppercase tracking-[0.2em] font-bold text-sm focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#141414]"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Open mijn kluis"}
            </Button>

            <div className="pt-4 border-t border-tw grid grid-cols-2 gap-3 text-center">
              <div className="font-mono text-[10px] tracking-[0.2em] text-tw-muted uppercase">
                Alleen op uitnodiging
              </div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-tw-muted uppercase">
                Persoonlijke toegang
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
